import React, { useState, useEffect } from 'react';
import { FaEdit, FaLock, FaUnlock, FaCheckCircle, FaTimes } from 'react-icons/fa';
import { doc, updateDoc, getDoc } from 'firebase/firestore';
import { db } from '../../../../firebase';
import { useNotifications } from '../../../notifications/NotificationsProvider';
import '../styles/companion-views.css';
import './companion-owner-controls.css';

/**
 * CompanionOwnerControls - Owner management panel for evidence
 * Shows edit controls, privacy settings, and file information
 * CRITICAL: Only shown when currentUser is the evidence owner
 */
const CompanionOwnerControls = ({ data = {} }) => {
  const { notify } = useNotifications();
  
  // Extract data from props
  const { evidence = {}, currentUser, onUpdateSuccess, casefileId } = data;
  
  // State management
  const [isEditingTitle, setIsEditingTitle] = useState(false);
  const [editedTitle, setEditedTitle] = useState(evidence.fileName || '');
  const [isSavingTitle, setIsSavingTitle] = useState(false);
  const [isPublic, setIsPublic] = useState(evidence.isPublic || false);
  const [isSavingPrivacy, setIsSavingPrivacy] = useState(false);
  const [casefileIsPublic, setCasefileIsPublic] = useState(null);
  const [isEditingSummary, setIsEditingSummary] = useState(false);
  const [editedSummary, setEditedSummary] = useState(evidence.summary || '');
  const [isSavingSummary, setIsSavingSummary] = useState(false);

  // Fetch fresh evidence data on mount to get accurate privacy status
  useEffect(() => {
    const fetchEvidenceData = async () => {
      if (!casefileId || !evidence?.id) return;
      
      try {
        const evidenceRef = doc(db, 'casefiles', casefileId, 'evidence', evidence.id);
        const evidenceDoc = await getDoc(evidenceRef);
        
        if (evidenceDoc.exists()) {
          const evidenceData = evidenceDoc.data();
          
          // Update all states with fresh data from Firestore
          if (evidenceData.fileName) {
            setEditedTitle(evidenceData.fileName);
          }
          if (evidenceData.isPublic !== undefined) {
            setIsPublic(evidenceData.isPublic);
          }
          if (evidenceData.summary !== undefined) {
            setEditedSummary(evidenceData.summary || '');
          }
        }
      } catch (error) {
        console.error('Error fetching evidence data:', error);
        // Fall back to props data
        if (evidence?.fileName) {
          setEditedTitle(evidence.fileName);
        }
        if (evidence?.isPublic !== undefined) {
          setIsPublic(evidence.isPublic);
        }
        if (evidence?.summary !== undefined) {
          setEditedSummary(evidence.summary || '');
        }
      }
    };

    fetchEvidenceData();
  }, [evidence?.id, casefileId, evidence?.fileName, evidence?.isPublic, evidence?.summary]);

  // Check casefile privacy status
  useEffect(() => {
    const checkCasefilePrivacy = async () => {
      if (!casefileId) return;
      
      try {
        const casefileDoc = await getDoc(doc(db, 'casefiles', casefileId));
        if (casefileDoc.exists()) {
          setCasefileIsPublic(casefileDoc.data().isPublic || false);
        }
      } catch (error) {
        console.error('Error checking casefile privacy:', error);
      }
    };

    checkCasefilePrivacy();
  }, [casefileId]);

  // Format date - defined before ownership check for use in non-owner view
  const formatDate = (date) => {
    if (!date) return 'Never';
    const d = date.toDate ? date.toDate() : new Date(date);
    const now = new Date();
    const diff = now - d;
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));

    if (hours < 1) return 'Just now';
    if (hours < 24) return `${hours} hour${hours > 1 ? 's' : ''} ago`;
    if (days < 7) return `${days} day${days > 1 ? 's' : ''} ago`;
    return d.toLocaleDateString();
  };

  // Format file size - defined before ownership check for use in non-owner view
  const formatFileSize = (bytes) => {
    if (!bytes) return 'Unknown';
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(1024));
    return Math.round(bytes / Math.pow(1024, i) * 100) / 100 + ' ' + sizes[i];
  };

  // Check if user is owner
  const isOwner = currentUser && evidence && (
    currentUser.uid === evidence.createdBy || 
    currentUser.uid === evidence.userId
  );

  // If not owner, show read-only evidence information
  if (!isOwner) {
    return (
      <div className="companion-owner-controls non-owner-view">
        
        {/* Evidence Information Section - Read Only */}
        <div className="control-section info-section">
          <div className="section-header">
            <span className="section-icon">📋</span>
            <h4>Evidence Information</h4>
          </div>
          
          <div className="info-list">
            <div className="info-row">
              <span className="info-row-label">File Type</span>
              <span className="info-row-value">{evidence.contentType || 'Unknown'}</span>
            </div>
            <div className="info-row">
              <span className="info-row-label">File Size</span>
              <span className="info-row-value">{formatFileSize(evidence.fileSize)}</span>
            </div>
            <div className="info-row">
              <span className="info-row-label">Uploaded</span>
              <span className="info-row-value">{formatDate(evidence.createdAt)}</span>
            </div>
            <div className="info-row">
              <span className="info-row-label">Evidence ID</span>
              <span className="info-row-value evidence-id">{evidence.id}</span>
            </div>
          </div>
        </div>

        {/* Evidence Summary Section - Read Only */}
        {evidence.summary && (
          <div className="control-section summary-section">
            <div className="section-header">
              <span className="section-icon">📄</span>
              <h4>Evidence Summary</h4>
            </div>
            
            <div className="summary-display">
              <p className="summary-text">{evidence.summary}</p>
            </div>
          </div>
        )}

      </div>
    );
  }

  // Handle title edit
  const handleStartEditTitle = () => {
    setIsEditingTitle(true);
    setEditedTitle(evidence.fileName || '');
  };

  const handleCancelEditTitle = () => {
    setIsEditingTitle(false);
    setEditedTitle(evidence.fileName || '');
  };

  const handleSaveTitle = async () => {
    if (!editedTitle.trim()) {
      notify('Title cannot be empty', 'error');
      return;
    }

    if (editedTitle === evidence.fileName) {
      setIsEditingTitle(false);
      return;
    }

    setIsSavingTitle(true);

    try {
      const evidenceRef = doc(db, 'casefiles', casefileId, 'evidence', evidence.id);
      await updateDoc(evidenceRef, {
        fileName: editedTitle.trim(),
        lastModified: new Date()
      });

      notify('Evidence title updated successfully', 'success');
      setIsEditingTitle(false);

      // Call parent update callback if provided
      if (onUpdateSuccess) {
        onUpdateSuccess({ fileName: editedTitle.trim() });
      }
    } catch (error) {
      console.error('Error updating evidence title:', error);
      notify('Failed to update title', 'error');
    } finally {
      setIsSavingTitle(false);
    }
  };

  // Handle privacy toggle
  const handleTogglePrivacy = async () => {
    if (isSavingPrivacy) return; // Prevent double-clicks

    const newPrivacyStatus = !isPublic;

    console.log('🔒 Privacy toggle clicked:', {
      current: isPublic,
      new: newPrivacyStatus,
      evidenceId: evidence.id,
      casefileId: casefileId
    });

    // Check if casefile is public when trying to make evidence public
    if (newPrivacyStatus && casefileIsPublic === false) {
      notify('Cannot make evidence public: Parent casefile is private', 'warning');
      return;
    }

    // Optimistically update UI
    setIsPublic(newPrivacyStatus);
    setIsSavingPrivacy(true);

    try {
      const evidenceRef = doc(db, 'casefiles', casefileId, 'evidence', evidence.id);
      
      console.log('🔒 Updating Firestore:', {
        path: `casefiles/${casefileId}/evidence/${evidence.id}`,
        isPublic: newPrivacyStatus
      });
      
      await updateDoc(evidenceRef, {
        isPublic: newPrivacyStatus,
        lastModified: new Date()
      });

      console.log('✅ Privacy updated successfully in Firestore');

      notify(
        `Evidence is now ${newPrivacyStatus ? 'public' : 'private'}`,
        'success'
      );

      // Call parent update callback if provided to refresh jobs data
      if (onUpdateSuccess) {
        onUpdateSuccess({ isPublic: newPrivacyStatus });
      }

      // Also verify the update worked
      const verifyDoc = await getDoc(evidenceRef);
      if (verifyDoc.exists()) {
        const actualStatus = verifyDoc.data().isPublic;
        console.log('🔍 Verified privacy status:', actualStatus);
        if (actualStatus !== newPrivacyStatus) {
          console.error('⚠️ Privacy mismatch! Expected:', newPrivacyStatus, 'Got:', actualStatus);
          setIsPublic(actualStatus); // Update to actual value
        }
      }
    } catch (error) {
      console.error('❌ Error updating privacy:', error);
      notify('Failed to update privacy setting', 'error');
      // Revert the checkbox on error
      setIsPublic(!newPrivacyStatus);
    } finally {
      setIsSavingPrivacy(false);
    }
  };


  // Handle summary edit
  const handleStartEditSummary = () => {
    setIsEditingSummary(true);
    setEditedSummary(evidence.summary || '');
  };

  const handleCancelEditSummary = () => {
    setIsEditingSummary(false);
    setEditedSummary(evidence.summary || '');
  };

  const handleSaveSummary = async () => {
    setIsSavingSummary(true);

    try {
      const evidenceRef = doc(db, 'casefiles', casefileId, 'evidence', evidence.id);
      await updateDoc(evidenceRef, {
        summary: editedSummary.trim(),
        lastModified: new Date()
      });

      notify('Evidence summary updated successfully', 'success');
      setIsEditingSummary(false);

      // Call parent update callback if provided
      if (onUpdateSuccess) {
        onUpdateSuccess({ summary: editedSummary.trim() });
      }
    } catch (error) {
      console.error('Error updating evidence summary:', error);
      notify('Failed to update summary', 'error');
    } finally {
      setIsSavingSummary(false);
    }
  };

  return (
    <div className="companion-owner-controls">

      {/* Title Section */}
      <div className="control-section">
        <div className="section-header">
          <FaEdit className="section-icon" />
          <h4>Evidence Title</h4>
        </div>
        
        {isEditingTitle ? (
          <div className="edit-title-container">
            <input
              type="text"
              value={editedTitle}
              onChange={(e) => setEditedTitle(e.target.value)}
              className="edit-title-input"
              placeholder="Enter evidence title..."
              disabled={isSavingTitle}
              autoFocus
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleSaveTitle();
                if (e.key === 'Escape') handleCancelEditTitle();
              }}
            />
            <div className="edit-title-actions">
              <button
                className="btn-save"
                onClick={handleSaveTitle}
                disabled={isSavingTitle}
              >
                {isSavingTitle ? (
                  'Saving...'
                ) : (
                  <>
                    <FaCheckCircle /> Save
                  </>
                )}
              </button>
              <button
                className="btn-cancel"
                onClick={handleCancelEditTitle}
                disabled={isSavingTitle}
              >
                <FaTimes /> Cancel
              </button>
            </div>
          </div>
        ) : (
          <div className="title-display-container">
            <div className="title-display">
              <span className="current-title">{evidence.fileName || 'Untitled Evidence'}</span>
            </div>
            <button
              className="btn-edit-title"
              onClick={handleStartEditTitle}
            >
              <FaEdit /> Edit Title
            </button>
          </div>
        )}
      </div>

      {/* Privacy Section */}
      <div className="control-section">
        <div className="section-header">
          {isPublic ? (
            <FaUnlock className="section-icon" />
          ) : (
            <FaLock className="section-icon" />
          )}
          <h4>Privacy</h4>
        </div>

        <div className="privacy-toggle-container">
          <div className="privacy-toggle-row">
            <div className="privacy-toggle-label">
              <span className={`privacy-label-text ${isPublic ? 'public' : 'private'}`}>
                {isPublic ? 'Public' : 'Private'}
              </span>
              <span className="privacy-label-description">
                {isPublic 
                  ? 'Anyone can view and search'
                  : 'Only you can access'}
              </span>
            </div>
            
            <div 
              className={`toggle-switch ${isSavingPrivacy ? 'disabled' : ''}`}
              onClick={handleTogglePrivacy}
            >
              <input
                type="checkbox"
                checked={isPublic}
                onChange={() => {}} // Controlled by onClick on parent
                disabled={isSavingPrivacy}
                readOnly
              />
              <span className="toggle-slider"></span>
            </div>
          </div>

          {!isPublic && casefileIsPublic === false && (
            <div className="privacy-note">
              <FaLock />
              <span>Parent casefile is also private</span>
            </div>
          )}
        </div>
      </div>

      {/* Evidence Information Section */}
      <div className="control-section info-section">
        <div className="section-header">
          <span className="section-icon">📋</span>
          <h4>Evidence Information</h4>
        </div>
        
        <div className="info-list">
          <div className="info-row">
            <span className="info-row-label">File Type</span>
            <span className="info-row-value">{evidence.contentType || 'Unknown'}</span>
          </div>
          <div className="info-row">
            <span className="info-row-label">File Size</span>
            <span className="info-row-value">{formatFileSize(evidence.fileSize)}</span>
          </div>
          <div className="info-row">
            <span className="info-row-label">Uploaded</span>
            <span className="info-row-value">{formatDate(evidence.createdAt)}</span>
          </div>
          <div className="info-row">
            <span className="info-row-label">Evidence ID</span>
            <span className="info-row-value evidence-id">{evidence.id}</span>
          </div>
        </div>
      </div>

      {/* Evidence Summary Section */}
      <div className="control-section summary-section">
        <div className="section-header">
          <FaEdit className="section-icon" />
          <h4>Evidence Summary</h4>
        </div>
        
        {isEditingSummary ? (
          <div className="edit-summary-container">
            <textarea
              value={editedSummary}
              onChange={(e) => setEditedSummary(e.target.value)}
              className="edit-summary-textarea"
              placeholder="Add a summary or description for this evidence..."
              disabled={isSavingSummary}
              rows={6}
              autoFocus
            />
            <div className="edit-summary-actions">
              <button
                className="btn-save"
                onClick={handleSaveSummary}
                disabled={isSavingSummary}
              >
                {isSavingSummary ? (
                  'Saving...'
                ) : (
                  <>
                    <FaCheckCircle /> Save
                  </>
                )}
              </button>
              <button
                className="btn-cancel"
                onClick={handleCancelEditSummary}
                disabled={isSavingSummary}
              >
                <FaTimes /> Cancel
              </button>
            </div>
          </div>
        ) : (
          <div className="summary-display-container">
            <div className="summary-display">
              {editedSummary ? (
                <p className="summary-text">{editedSummary}</p>
              ) : (
                <p className="summary-placeholder">No summary provided. Click edit to add one.</p>
              )}
            </div>
            <button
              className="btn-edit-summary"
              onClick={handleStartEditSummary}
            >
              <FaEdit /> {editedSummary ? 'Edit Summary' : 'Add Summary'}
            </button>
          </div>
        )}
      </div>

    </div>
  );
};

export default CompanionOwnerControls;

